/**
 * Image セクション — 3 variants
 *
 *   gallery_carousel: 1 枚ずつ表示、下部にドットナビゲータ + 左右矢印
 *                     資料側の `image / carousel` バリアントと同等の挙動
 *   grid_3col:        3 列グリッド (画像 3-6 枚をタイル表示)
 *   full_width:       フル幅 1 枚 + キャプション
 *
 * すべて CSS 変数 (var(--ls-*)) を使用、hardcoded 色は禁止。
 */
import { useEffect, useState, type CSSProperties } from 'react';

import type { ImageContent } from '../schemas/image';
import {
  Body,
  Eyebrow,
  H2,
  SectionFrame,
  VisualPlaceholder,
} from './primitives';

interface IP {
  content: ImageContent;
}

/**
 * セクション上部のヘッダー（eyebrow / headline / subheadline）。
 * 全項目空なら null を返してマージン消費もしない。
 */
function ImageHeader({ content }: { content: ImageContent }) {
  const hasAny =
    Boolean(content.eyebrow) ||
    Boolean(content.headline) ||
    Boolean(content.subheadline);
  if (!hasAny) return null;
  return (
    <div style={{ textAlign: 'center', marginBottom: 32 }}>
      {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
      {content.headline && <H2>{content.headline}</H2>}
      {content.subheadline && (
        <Body
          style={{
            marginTop: 8,
            color: 'var(--ls-text-muted)',
            maxWidth: 640,
            marginInline: 'auto',
          }}
        >
          {content.subheadline}
        </Body>
      )}
    </div>
  );
}

/** ───── gallery_carousel: 1 枚表示 + ドット / 矢印切替 ───── */
function ImageGalleryCarousel({ content }: IP) {
  const items = content.items ?? [];
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (active >= items.length) setActive(0);
  }, [items.length, active]);

  if (items.length === 0) {
    return (
      <SectionFrame>
        <ImageHeader content={content} />
        <div style={{ maxWidth: 960, marginInline: 'auto' }}>
          <VisualPlaceholder aspectRatio="16 / 10" label="画像未設定" />
        </div>
      </SectionFrame>
    );
  }

  const go = (dir: -1 | 1) => {
    setActive((i) => (i + dir + items.length) % items.length);
  };

  const navBtn: CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.45)',
    color: '#fff',
    border: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
    zIndex: 2,
  };

  return (
    <SectionFrame>
      <ImageHeader content={content} />
      <div style={{ maxWidth: 960, marginInline: 'auto' }}>
        <div style={{ position: 'relative' }}>
          {items.map((src, i) => (
            <div
              key={i}
              style={{
                display: i === active ? 'block' : 'none',
              }}
            >
              <VisualPlaceholder
                src={src || null}
                alt={content.caption ?? ''}
                aspectRatio="16 / 10"
                label={`画像 ${i + 1}`}
                fit={(content.imageFit ?? 'cover') as 'cover' | 'contain' | 'natural'}
              />
            </div>
          ))}
          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="前の画像"
                style={{ ...navBtn, left: 12 }}
              >
                <ChevronLeftIcon />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="次の画像"
                style={{ ...navBtn, right: 12 }}
              >
                <ChevronRightIcon />
              </button>
            </>
          )}
        </div>
        {items.length > 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 8,
              marginTop: 16,
            }}
          >
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`画像 ${i + 1} を表示`}
                style={{
                  width: i === active ? 10 : 8,
                  height: i === active ? 10 : 8,
                  borderRadius: '50%',
                  border: 0,
                  background:
                    i === active
                      ? 'var(--ls-text)'
                      : 'var(--ls-border, rgba(0,0,0,0.2))',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </div>
        )}
        {content.caption && (
          <p
            style={{
              textAlign: 'center',
              fontSize: '0.85rem',
              color: 'var(--ls-text-muted)',
              marginTop: 16,
            }}
          >
            {content.caption}
          </p>
        )}
      </div>
    </SectionFrame>
  );
}

/** ───── grid_3col: 3 列グリッド ───── */
function ImageGrid3col({ content }: IP) {
  const items = content.items ?? [];
  return (
    <SectionFrame>
      <ImageHeader content={content} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
        }}
      >
        {items.map((src, i) => (
          <VisualPlaceholder
            key={i}
            src={src || null}
            alt=""
            aspectRatio="1 / 1"
            label={`画像 ${i + 1}`}
            fit={(content.imageFit ?? 'cover') as 'cover' | 'contain' | 'natural'}
          />
        ))}
      </div>
      {content.caption && (
        <p
          style={{
            textAlign: 'center',
            fontSize: '0.85rem',
            color: 'var(--ls-text-muted)',
            marginTop: 16,
          }}
        >
          {content.caption}
        </p>
      )}
    </SectionFrame>
  );
}

/** ───── full_width: フル幅 1 枚 ───── */
function ImageFullWidth({ content }: IP) {
  const first = (content.items ?? [])[0];
  return (
    <SectionFrame>
      <ImageHeader content={content} />
      <div style={{ maxWidth: 1100, marginInline: 'auto' }}>
        <VisualPlaceholder
          src={first || null}
          alt={content.caption ?? ''}
          aspectRatio="16 / 9"
          label="メイン画像"
          fit={(content.imageFit ?? 'cover') as 'cover' | 'contain' | 'natural'}
        />
        {content.caption && (
          <p
            style={{
              textAlign: 'center',
              fontSize: '0.85rem',
              color: 'var(--ls-text-muted)',
              marginTop: 12,
            }}
          >
            {content.caption}
          </p>
        )}
      </div>
    </SectionFrame>
  );
}

function ChevronLeftIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export const imageVariants = {
  gallery_carousel: ImageGalleryCarousel,
  grid_3col: ImageGrid3col,
  full_width: ImageFullWidth,
};
